import { Box, Center, SimpleGrid, Text } from '@chakra-ui/react'
import {
  endOfMonth,
  format,
  getDay,
  isAfter,
  isBefore,
  isToday,
  startOfMonth,
} from 'date-fns'
import { Options, Returns } from 'use-lilius'

import { useIsDark } from '../../hooks/useIsDark'

type LilusTypes = Returns & Options

interface SingleSelectCalendarProps {
  lilus: LilusTypes
  onUpdate?: (day: Date | undefined) => void
  date: Date
  pillSelected: string
}

export const SingleSelectCalendar = ({
  lilus,
  onUpdate = () => {},
  pillSelected = 'equals',
}: SingleSelectCalendarProps) => {
  const isDark = useIsDark()
  const { calendar, inRange, isSelected, selected, toggle, viewing } = lilus

  const numWeeks = calendar[0].length
  const gridHeight = 31 + numWeeks * 31 + numWeeks * 4 // header + date rows + gaps
  const gapX = 10
  const barExtension = {
    w: 31 + gapX,
    h: 31,
    left: -gapX / 2,
    top: 0,
  }

  const clipPath = (pillSelected: string) => {
    if (pillSelected === 'lessThan')
      return 'polygon(0 0, 50% 0, 50% 100%, 0% 100%)'
    if (pillSelected === 'greaterThan')
      return 'polygon(100% 0, 50% 0, 50% 100%, 100% 100%)'
    return 'polygon(0 100%, 0% 100%, 100% 100%)'
  }

  const isBlackCirclePill = (p: string) =>
    p === 'lessThan' || p === 'greaterThan' || p === 'notEqual'

  const backgroundColor = ({
    pillSelected,
    day,
    isInRange,
  }: {
    pillSelected: String
    day: Date
    isInRange: Boolean
  }) => {
    const isSelectedDate = day.toDateString() === selected[0]?.toDateString()
    if (isSelectedDate && isBlackCirclePill(pillSelected as string)) {
      return isDark ? 'presets.turquoise.50' : 'presets.neutral.100'
    }
    if (isSelectedDate && pillSelected === 'equals') {
      return isDark ? 'presets.turquoise.50' : 'presets.turquoise.100'
    }
    if (
      (pillSelected === 'greaterThan' && isBefore(selected[0], day)) ||
      (pillSelected === 'lessThan' && isAfter(selected[0], day)) ||
      isSelectedDate
    ) {
      if (isInRange) {
        return isDark ? 'presets.turquoise.80' : 'presets.turquoise.20'
      }
    }
  }

  const selectedColor = (isSelectedDay: boolean, inHighlightRange: boolean) => {
    if (isDark && (isSelectedDay || inHighlightRange)) {
      return 'presets.neutral.0'
    }
    if (isSelectedDay) return 'white'
    return 'presets.turquoise.100'
  }

  const isInHighlightRange = (day: Date) =>
    !!backgroundColor({
      pillSelected,
      day,
      isInRange: inRange(day, startOfMonth(viewing), endOfMonth(viewing)),
    })

  return (
    <SimpleGrid
      height={`${gridHeight}px`}
      gap="4px 10px"
      w="100%"
      templateColumns="repeat(7, minmax(0, 1fr))"
      gridAutoRows="1fr"
      marginBottom="20px"
    >
      {calendar[0][0].map(day => (
        <Box
          height="31px"
          key={`${day}`}
          alignContent="center"
          justifyContent="center"
        >
          <Center>
            <Text color="presets.neutral.70" fontWeight="600" fontSize="12px">
              {['S', 'M', 'T', 'W', 'T', 'F', 'S'][getDay(day)]}
            </Text>
          </Center>
        </Box>
      ))}
      {calendar[0].map(week => {
        return week.map(day => {
          const isInRange = inRange(
            day,
            startOfMonth(viewing),
            endOfMonth(viewing),
          )
          return (
            <Box
              position="relative"
              width="31px"
              height="31px"
              key={`${day}`}
              cursor="pointer"
              overflow="visible"
              onClick={() => {
                if (!isInRange) {
                  return
                }
                toggle(day, true)
                onUpdate(day)
              }}
              alignContent="center"
              justifyContent="center"
            >
              {day.toDateString() === selected[0]?.toDateString() ? (
                <Box
                  background={
                    isDark ? 'presets.turquoise.80' : 'presets.turquoise.20'
                  }
                  width={`${barExtension.w}px`}
                  height={`${barExtension.h}px`}
                  left={`${barExtension.left}px`}
                  top={`${barExtension.top}px`}
                  clipPath={clipPath(pillSelected)}
                  position="absolute"
                />
              ) : null}
              <Box
                position="absolute"
                width={isSelected(day) ? '31px' : `${barExtension.w}px`}
                height={isSelected(day) ? '31px' : `${barExtension.h}px`}
                left={isSelected(day) ? '0' : `${barExtension.left}px`}
                top={isSelected(day) ? '0' : `${barExtension.top}px`}
                background={backgroundColor({ pillSelected, day, isInRange })}
                rounded={isSelected(day) ? 'full' : 'none'}
              />
              <Box position="relative">
                <Center
                  height="31px"
                  _hover={{
                    ...(isInRange &&
                      !isSelected(day) && {
                        background: isDark
                          ? 'presets.turquoise.50'
                          : 'presets.turquoise.10',
                        border: isDark ? 'none' : '1px solid darkgreen',
                        rounded: 'full',
                        height: '31px',
                        width: '31px',
                      }),
                  }}
                >
                  <Text
                    fontWeight={isInHighlightRange(day) ? '600' : '500'}
                    fontSize="14px"
                    color={selectedColor(
                      isSelected(day),
                      isInHighlightRange(day),
                    )}
                  >
                    {isInRange ? `${format(day, 'd')}` : ''}
                  </Text>
                </Center>
                <Box
                  background={
                    isDark
                      ? 'presets.turquoise.50'
                      : isSelected(day)
                        ? 'white'
                        : 'black'
                  }
                  position="absolute"
                  width="6px"
                  height="6px"
                  left="50%"
                  top="100%"
                  transform="translate(-50%, 4px)"
                  rounded="full"
                  hidden={
                    !isToday(day) || day.getMonth() !== viewing.getMonth()
                  }
                />
              </Box>
            </Box>
          )
        })
      })}
    </SimpleGrid>
  )
}
